import { lazy, Suspense } from 'react';
import { RouteObject } from 'react-router-dom';
import NotFound from '../pages/NotFound';
import MainLayout from '../components/layout/MainLayout';
import PricingAdminRedirect from './PricingAdminRedirect';
import { localizedPath, type StoreLanguage } from '../i18n/core';

const HomePage = lazy(() => import('../pages/home/page'));
const ProductsPage = lazy(() => import('../pages/products/page'));
const ProductDetailPage = lazy(() => import('../pages/product-detail/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const BlogPage = lazy(() => import('../pages/blog/page'));
const BlogDetailPage = lazy(() => import('../pages/blog-detail/page'));
const BlogArchivePage = lazy(() => import('../pages/blog-archive/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const FAQPage = lazy(() => import('../pages/faq/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const CookiesPage = lazy(() => import('../pages/cookies/page'));
const CheckoutPage = lazy(() => import('../pages/checkout/page'));
const WishlistPage = lazy(() => import('../pages/wishlist/page'));
const AdminPage = lazy(() => import('../pages/admin/page'));
const OrderConfirmationPage = lazy(() => import('../pages/order-confirmation/page'));
const OrderSuccessPage = lazy(() => import('../pages/order-success/page'));
const OrderTrackingPage = lazy(() => import('../pages/order-tracking/page'));
const RecipesPage = lazy(() => import('../pages/recipes/page'));
const RecipeDetailPage = lazy(() => import('../pages/recipe-detail/page'));
const BundlesPage = lazy(() => import('../pages/bundles/page'));
const OffersPage = lazy(() => import('../pages/offers/page'));
const BundleDetailPage = lazy(() => import('../pages/bundle-detail/page'));
const CreatePackPage = lazy(() => import('../pages/create-pack/page'));
const RegisterPage = lazy(() => import('../pages/register/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const ForgotPasswordPage = lazy(() => import('../pages/forgot-password/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const AccountPage = lazy(() => import('../pages/account/page'));
const PromotionsListPage = lazy(() => import('../pages/admin-promotions/page'));
const PromotionFormPageRoute = lazy(() => import('../pages/admin-promotion-form/page'));
const SitemapPage = lazy(() => import('../pages/sitemap/page'));
const LoyaltyPage = lazy(() => import('../pages/loyalty/page'));
const MyPointsPage = lazy(() => import('../pages/my-points/page'));
const ReturnsPage = lazy(() => import('../pages/returns/page'));
const ShippingPage = lazy(() => import('../pages/shipping/page'));

import PageLoader from './PageLoader';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    children: [
      { 
        path: '/', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <HomePage />
          </Suspense>
        ) 
      },
      { 
        path: '/products', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductsPage />
          </Suspense>
        ) 
      },
      { 
        path: '/product/:slug', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <ProductDetailPage />
          </Suspense>
        ) 
      },
      { 
        path: '/bundles', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <BundlesPage />
          </Suspense>
        ) 
      },
      { 
        path: '/bundles/:slug', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <BundleDetailPage />
          </Suspense>
        ) 
      },
      { 
        path: '/crea-tu-pack', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <CreatePackPage />
          </Suspense>
        ) 
      },
      { 
        path: '/recipes', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <RecipesPage />
          </Suspense>
        ) 
      },
      { 
        path: '/recipes/:slug', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <RecipeDetailPage />
          </Suspense>
        ) 
      },
      { 
        path: '/blog', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogPage />
          </Suspense>
        ) 
      },
      { 
        path: '/blog/archivo', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogArchivePage />
          </Suspense>
        ) 
      },
      { 
        path: '/blog/:slug', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <BlogDetailPage />
          </Suspense>
        ) 
      },
      { 
        path: '/about-us', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <AboutPage />
          </Suspense>
        ) 
      },
      { 
        path: '/contact', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <ContactPage />
          </Suspense>
        ) 
      },
      { 
        path: '/faq', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <FAQPage />
          </Suspense>
        ) 
      },
      { 
        path: '/offers', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <OffersPage />
          </Suspense>
        ) 
      },
      { 
        path: '/wishlist', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <WishlistPage />
          </Suspense>
        ) 
      },
      { 
        path: '/account', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <AccountPage />
          </Suspense>
        ) 
      },
      { 
        path: '/checkout', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <CheckoutPage />
          </Suspense>
        ) 
      },
      { 
        path: '/order-confirmation', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <OrderConfirmationPage />
          </Suspense>
        ) 
      },
      { 
        path: '/order-success', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <OrderSuccessPage />
          </Suspense>
        ) 
      },
      { 
        path: '/order-tracking', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <OrderTrackingPage />
          </Suspense>
        ) 
      },
      { 
        path: '/privacy', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        ) 
      },
      { 
        path: '/terms', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        ) 
      },
      { 
        path: '/cookies', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <CookiesPage />
          </Suspense>
        ) 
      },
      { 
        path: '/sitemap', 
        element: (
          <Suspense fallback={<PageLoader />}>
            <SitemapPage />
          </Suspense>
        ) 
      },
      {
        path: '/fidelidad',
        element: (
          <Suspense fallback={<PageLoader />}>
            <LoyaltyPage />
          </Suspense>
        )
      },
      {
        path: '/mis-puntos',
        element: (
          <Suspense fallback={<PageLoader />}>
            <MyPointsPage />
          </Suspense>
        )
      },
      {
        path: '/returns',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ReturnsPage />
          </Suspense>
        )
      },
      {
        path: '/shipping',
        element: (
          <Suspense fallback={<PageLoader />}>
            <ShippingPage />
          </Suspense>
        )
      },
      { path: '*', element: <NotFound /> },
    ],
  },
  { 
    path: '/login', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <LoginPage />
      </Suspense>
    ) 
  },
  { 
    path: '/register', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <RegisterPage />
      </Suspense>
    ) 
  },
  { 
    path: '/forgot-password', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <ForgotPasswordPage />
      </Suspense>
    ) 
  },
  { 
    path: '/reset-password', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <ResetPasswordPage />
      </Suspense>
    ) 
  },
  { 
    path: '/admin', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <AdminPage />
      </Suspense>
    ) 
  },
  { 
    path: '/admin/promotions', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <PromotionsListPage />
      </Suspense>
    ) 
  },
  { 
    path: '/admin/promotions/new', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <PromotionFormPageRoute />
      </Suspense>
    ) 
  },
  { 
    path: '/admin/promotions/:id', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <PromotionFormPageRoute />
      </Suspense>
    ) 
  },
  { 
    path: '/admin/pricing/packs-history', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <PricingAdminRedirect section="packs-history"/>
      </Suspense>
    ) 
  },
  { 
    path: '/admin/pricing/packs-latest', 
    element: (
      <Suspense fallback={<PageLoader />}>
        <PricingAdminRedirect section="packs-latest"/>
      </Suspense>
    ) 
  },
];

function localeRoute(route: RouteObject, language: StoreLanguage): RouteObject | null {
  if (route.path?.startsWith('/admin')) return null;
  return {
    ...route,
    ...(route.path?.startsWith('/') ? { path: localizedPath(route.path, language) } : {}),
    ...(route.children ? { children: route.children.map(child => localeRoute(child, language)).filter(Boolean) } : {}),
  } as RouteObject;
}

export default [...routes, ...(['en', 'de'] as const).flatMap(language => routes.map(route => localeRoute(route, language)).filter(Boolean))];
